<template>
  <section class="admin-orders" aria-labelledby="admin-orders-title">
    <div class="queue-heading">
      <div>
        <h2 id="admin-orders-title" class="text-h5 font-weight-medium mb-1">Bestellungen verwalten</h2>
        <p class="queue-subtitle mb-0">Offene Aufträge priorisieren, prüfen und weiterbearbeiten.</p>
      </div>
      <v-chip small outlined color="grey lighten-1">{{ filteredOrders.length }} angezeigt</v-chip>
    </div>

    <div class="status-kpis" role="group" aria-label="Bestellungen nach Status filtern">
      <button
        v-for="stat in stats"
        :key="stat.value"
        type="button"
        class="status-kpi"
        :class="{ active: filterStatus === stat.value }"
        :aria-pressed="filterStatus === stat.value ? 'true' : 'false'"
        :aria-label="`${stat.label} filtern, ${stat.countLabel} Bestellungen`"
        @click="setStatusFilter(stat.value)">
        <span class="kpi-icon" :style="{ color: stat.color, backgroundColor: stat.background }">
          <v-icon small :color="stat.iconColor">{{ stat.icon }}</v-icon>
        </span>
        <span class="kpi-copy">
          <span class="kpi-label">{{ stat.label }}</span>
          <strong class="kpi-count">{{ stat.countLabel }}</strong>
        </span>
        <v-icon v-if="filterStatus === stat.value" small color="primary" class="kpi-selected">mdi-check-circle</v-icon>
      </button>
    </div>

    <v-card dark flat class="queue-toolbar mb-4">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        label="Bestellung suchen"
        placeholder="Nummer, Studio, Person oder Artikel"
        outlined
        dense
        dark
        clearable
        hide-details
        class="queue-search" />

      <v-select
        :value="filterStudio"
        :items="studioOptions"
        item-text="name"
        item-value="_id"
        label="Studio"
        outlined
        dense
        dark
        hide-details
        class="queue-filter"
        @input="$emit('update:filterStudio', $event)" />

      <v-select
        :value="filterStatus"
        :items="statusOptions"
        label="Status"
        outlined
        dense
        dark
        hide-details
        class="queue-filter"
        @input="$emit('update:filterStatus', $event)" />

      <div class="toolbar-actions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="hasActiveFilters"
              icon
              large
              color="grey lighten-1"
              v-bind="attrs"
              v-on="on"
              aria-label="Alle Filter zurücksetzen"
              @click="resetFilters">
              <v-icon>mdi-filter-remove-outline</v-icon>
            </v-btn>
          </template>
          <span>Alle Filter zurücksetzen</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              large
              color="primary"
              :loading="loading"
              v-bind="attrs"
              v-on="on"
              aria-label="Bestellungen aktualisieren"
              @click="$emit('reload')">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Bestellungen aktualisieren</span>
        </v-tooltip>
      </div>
    </v-card>

    <div v-if="loading" class="queue-loading" aria-live="polite" aria-label="Bestellungen werden geladen">
      <v-skeleton-loader v-for="n in 4" :key="n" dark type="list-item-avatar-three-line" class="mb-2" />
    </div>

    <div v-else-if="orders.length === 0" class="queue-empty">
      <v-icon size="48" color="grey darken-1">mdi-clipboard-check-outline</v-icon>
      <div class="subtitle-1 font-weight-medium mt-3">Keine Bestellungen in dieser Auswahl</div>
      <div class="caption grey--text mt-1">Passe Studio oder Status an, um andere Aufträge zu sehen.</div>
      <v-btn v-if="hasActiveFilters" small outlined color="primary" class="mt-4" @click="resetFilters">Filter zurücksetzen</v-btn>
    </div>

    <div v-else-if="filteredOrders.length === 0" class="queue-empty">
      <v-icon size="48" color="grey darken-1">mdi-magnify-close</v-icon>
      <div class="subtitle-1 font-weight-medium mt-3">Keine passenden Bestellungen</div>
      <div class="caption grey--text mt-1">Passe Suche, Studio oder Status an.</div>
      <v-btn small outlined color="primary" class="mt-4" @click="resetFilters">Filter zurücksetzen</v-btn>
    </div>

    <template v-else>
      <v-card v-if="!$vuetify.breakpoint.smAndDown" dark flat class="orders-table-card">
        <v-data-table
          dark
          :headers="headers"
          :items="filteredOrders"
          item-key="_id"
          :items-per-page="25"
          sort-by="createdAt"
          :sort-desc="true"
          :mobile-breakpoint="0"
          class="orders-table"
          no-data-text="Keine Bestellungen"
          @click:row="openOrder">
          <template v-slot:item.reference="{ item }">
            <div class="reference-cell">
              <strong>{{ displayOrderNumber(item) }}</strong>
              <span v-if="item.bestellnummer">Intern #{{ shortId(item) }}</span>
            </div>
          </template>
          <template v-slot:item.createdAt="{ item }">
            <div class="date-cell">
              <span>{{ $moment(item.createdAt).format("DD.MM.YYYY") }}</span>
              <small>{{ $moment(item.createdAt).format("HH:mm") }} Uhr</small>
            </div>
          </template>
          <template v-slot:item.studio="{ item }">
            <span class="cell-strong">{{ item.studio ? item.studio.name : "-" }}</span>
          </template>
          <template v-slot:item.createdBy="{ item }">
            <div class="person-cell">
              <span>{{ creatorName(item) }}</span>
              <small v-if="item.createdBy && item.createdBy.email">{{ item.createdBy.email }}</small>
            </div>
          </template>
          <template v-slot:item.summary="{ item }">
            <div class="summary-cell">
              <span class="cell-strong">{{ totalItems(item) }} Artikel</span>
              <small>{{ itemSummary(item) }}</small>
            </div>
          </template>
          <template v-slot:item.activity="{ item }">
            <div class="activity-cell">
              <v-chip v-if="item.comments && item.comments.length" x-small outlined color="grey lighten-1">
                <v-icon left x-small>mdi-comment-text-multiple-outline</v-icon>{{ item.comments.length }}
              </v-chip>
              <span v-else class="muted-cell">–</span>
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="statusColor(item.status)" dark>
              <v-icon left x-small>{{ statusIcon(item.status) }}</v-icon
              >{{ statusLabel(item.status) }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="row-actions">
              <v-tooltip bottom v-if="hasPhysicalItems(item)">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    large
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    :aria-label="`Lieferschein für ${displayOrderNumber(item)} herunterladen`"
                    @click.stop="downloadDeliveryNote(item)">
                    <v-icon>mdi-file-pdf-box</v-icon>
                  </v-btn>
                </template>
                <span>Lieferschein herunterladen</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    large
                    color="grey lighten-1"
                    v-bind="attrs"
                    v-on="on"
                    :aria-label="`${displayOrderNumber(item)} öffnen`"
                    @click.stop="openOrder(item)">
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </template>
                <span>Bestellung öffnen</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <div v-else class="mobile-orders" aria-live="polite">
        <article
          v-for="item in filteredOrders"
          :key="item._id"
          class="mobile-order-card"
          role="button"
          tabindex="0"
          :aria-label="`${displayOrderNumber(item)}, ${statusLabel(item.status)}. Bestellung öffnen.`"
          @click="openOrder(item)"
          @keydown.enter="openOrder(item)"
          @keydown.space.prevent="openOrder(item)">
          <div class="mobile-order-top">
            <div class="reference-cell">
              <span class="eyebrow">Bestellung</span>
              <strong>{{ displayOrderNumber(item) }}</strong>
              <span v-if="item.bestellnummer">Intern #{{ shortId(item) }}</span>
            </div>
            <v-chip small :color="statusColor(item.status)" dark>
              <v-icon left x-small>{{ statusIcon(item.status) }}</v-icon
              >{{ statusLabel(item.status) }}
            </v-chip>
          </div>

          <div class="mobile-order-meta">
            <span><v-icon small color="grey lighten-1">mdi-store-outline</v-icon>{{ item.studio ? item.studio.name : "-" }}</span>
            <span><v-icon small color="grey lighten-1">mdi-account-outline</v-icon>{{ creatorName(item) }}</span>
            <span
              ><v-icon small color="grey lighten-1">mdi-calendar-blank-outline</v-icon>{{ $moment(item.createdAt).format("DD.MM.YYYY, HH:mm") }}</span
            >
          </div>

          <div class="mobile-order-summary">
            <div>
              <strong>{{ totalItems(item) }} Artikel</strong>
              <span>{{ itemSummary(item) }}</span>
            </div>
            <v-chip v-if="item.comments && item.comments.length" x-small outlined color="grey lighten-1">
              <v-icon left x-small>mdi-comment-text-multiple-outline</v-icon>{{ item.comments.length }}
            </v-chip>
          </div>

          <div class="mobile-card-actions">
            <v-btn
              v-if="hasPhysicalItems(item)"
              text
              color="primary"
              class="touch-action"
              :aria-label="`Lieferschein für ${displayOrderNumber(item)} herunterladen`"
              @click.stop="downloadDeliveryNote(item)">
              <v-icon left>mdi-file-pdf-box</v-icon>Lieferschein
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn text color="primary" class="touch-action" @click.stop="openOrder(item)">Details<v-icon right>mdi-chevron-right</v-icon></v-btn>
          </div>
        </article>
      </div>
    </template>

    <v-snackbar v-model="feedback.show" :color="feedback.color" :timeout="4500" bottom>
      {{ feedback.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="feedback.show = false">Schliessen</v-btn>
      </template>
    </v-snackbar>
  </section>
</template>

<script>
import api from "@/api"

export default {
  name: "AdminOrdersList",
  props: {
    orders: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    studioOptions: { type: Array, default: () => [] },
    filterStudio: { type: String, default: "all" },
    filterStatus: { type: String, default: "all" },
  },
  data() {
    return {
      searchQuery: "",
      feedback: { show: false, text: "", color: "error" },
      headers: [
        {
          text: "Bestellung",
          value: "reference",
          sortable: false,
          width: "150px",
        },
        { text: "Datum", value: "createdAt", width: "125px" },
        { text: "Studio", value: "studio", sortable: false },
        { text: "Bestellt von", value: "createdBy", sortable: false },
        { text: "Umfang", value: "summary", sortable: false },
        {
          text: "Aktivität",
          value: "activity",
          sortable: false,
          align: "center",
        },
        { text: "Status", value: "status", sortable: false },
        {
          text: "Aktionen",
          value: "actions",
          sortable: false,
          align: "end",
          width: "112px",
        },
      ],
      statusOptions: [
        { text: "Alle Status", value: "all" },
        { text: "Eingegangen", value: "received" },
        { text: "Bestätigt", value: "confirmed" },
        { text: "Unterwegs", value: "shipping" },
        { text: "Geliefert", value: "delivered" },
      ],
    }
  },
  computed: {
    hasActiveFilters() {
      return !!(this.searchQuery && this.searchQuery.trim()) || this.filterStudio !== "all" || this.filterStatus !== "all"
    },
    baseOrders() {
      const query = (this.searchQuery || "").trim().toLowerCase()
      return this.orders.filter((order) => {
        const studioId = order.studio && order.studio._id ? order.studio._id : order.studio
        if (this.filterStudio !== "all" && String(studioId) !== String(this.filterStudio)) return false
        if (!query) return true
        const haystack = [
          order._id,
          this.shortId(order),
          order.bestellnummer,
          order.studio && order.studio.name,
          this.creatorName(order),
          order.createdBy && order.createdBy.email,
          this.statusLabel(order.status),
          order.note,
          ...(order.items || []).map((item) => item.productTitle),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
        return haystack.includes(query)
      })
    },
    filteredOrders() {
      if (this.filterStatus === "all") return this.baseOrders
      return this.baseOrders.filter((order) => order.status === this.filterStatus)
    },
    stats() {
      const configs = [
        {
          label: "Alle",
          value: "all",
          color: "#cbd5e1",
          background: "rgba(203,213,225,.1)",
          iconColor: "blue-grey lighten-2",
          icon: "mdi-format-list-bulleted",
        },
        {
          label: "Eingegangen",
          value: "received",
          color: "#cbd5e1",
          background: "rgba(203,213,225,.1)",
          iconColor: "blue-grey lighten-2",
          icon: "mdi-inbox-arrow-down",
        },
        {
          label: "Bestätigt",
          value: "confirmed",
          color: "#fbbf24",
          background: "rgba(251,191,36,.1)",
          iconColor: "amber lighten-1",
          icon: "mdi-check-circle-outline",
        },
        {
          label: "Unterwegs",
          value: "shipping",
          color: "#60a5fa",
          background: "rgba(96,165,250,.1)",
          iconColor: "blue lighten-2",
          icon: "mdi-truck-fast-outline",
        },
        {
          label: "Geliefert",
          value: "delivered",
          color: "#4ade80",
          background: "rgba(74,222,128,.1)",
          iconColor: "green lighten-2",
          icon: "mdi-package-variant-closed-check",
        },
      ]

      return configs.map((config) => ({
        ...config,
        countLabel: config.value === "all" ? this.baseOrders.length : this.baseOrders.filter((order) => order.status === config.value).length,
      }))
    },
  },
  methods: {
    openOrder(order) {
      this.$emit("open-detail", order)
    },
    setStatusFilter(status) {
      const nextStatus = status !== "all" && this.filterStatus === status ? "all" : status
      this.$emit("update:filterStatus", nextStatus)
    },
    resetFilters() {
      this.searchQuery = ""
      if (this.filterStudio !== "all") this.$emit("update:filterStudio", "all")
      if (this.filterStatus !== "all") this.$emit("update:filterStatus", "all")
    },
    shortId(order) {
      return order && order._id ? order._id.slice(-6).toUpperCase() : "------"
    },
    displayOrderNumber(order) {
      return order.bestellnummer || `#${this.shortId(order)}`
    },
    creatorName(order) {
      if (!order.createdBy) return "Unbekannt"
      return `${order.createdBy.vorname || ""} ${order.createdBy.nachname || ""}`.trim() || "Unbekannt"
    },
    totalItems(order) {
      return (order.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0)
    },
    itemSummary(order) {
      const items = order.items || []
      if (items.length === 0) return "Keine Artikel"
      const first = items[0].productTitle || "Artikel"
      return items.length > 1 ? `${first} +${items.length - 1}` : first
    },
    statusColor(status) {
      if (status === "delivered") return "green darken-1"
      if (status === "shipping") return "blue darken-1"
      if (status === "confirmed") return "amber darken-3"
      return "blue-grey darken-1"
    },
    statusIcon(status) {
      if (status === "delivered") return "mdi-package-variant-closed-check"
      if (status === "shipping") return "mdi-truck-fast-outline"
      if (status === "confirmed") return "mdi-check-circle-outline"
      return "mdi-inbox-arrow-down"
    },
    statusLabel(status) {
      if (status === "delivered") return "Geliefert"
      if (status === "shipping") return "Unterwegs"
      if (status === "confirmed") return "Bestätigt"
      if (status === "received") return "Eingegangen"
      return "Unbekannt"
    },
    hasPhysicalItems(order) {
      return (order.items || []).some((item) => item.productType !== "digital")
    },
    showFeedback(text, color = "error") {
      this.feedback = { show: true, text, color }
    },
    async downloadDeliveryNote(order) {
      try {
        const orderId = order._id
        const filename = `Lieferschein_${this.shortId(order)}.pdf`
        await api.marketingShop.downloadOrderDeliveryNote(orderId, filename)
        this.showFeedback("Lieferschein wurde heruntergeladen.", "success")
      } catch (e) {
        console.error("Lieferschein Download fehlgeschlagen:", e)
        this.showFeedback("Lieferschein konnte nicht heruntergeladen werden.")
      }
    },
  },
}
</script>

<style scoped>
.admin-orders {
  width: 100%;
}

.queue-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.queue-subtitle {
  color: rgba(255, 255, 255, 0.58);
  font-size: 14px;
}

.status-kpis {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.status-kpi {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 74px;
  padding: 12px;
  color: #fff;
  font: inherit;
  text-align: left;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 160ms ease, background-color 160ms ease, transform 160ms ease;
}

.status-kpi:hover,
.status-kpi:focus-visible,
.status-kpi.active {
  background: rgba(255, 255, 255, 0.065);
  border-color: rgba(0, 150, 64, 0.58);
  outline: none;
}

.status-kpi:hover {
  transform: translateY(-1px);
}

.kpi-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  margin-right: 10px;
  border-radius: 10px;
}

.kpi-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.kpi-label {
  overflow: hidden;
  color: rgba(255, 255, 255, 0.58);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpi-count {
  color: #fff;
  font-size: 21px;
  line-height: 1.2;
}

.kpi-selected {
  position: absolute;
  top: 7px;
  right: 7px;
}

.queue-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.045) !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
}

.queue-search {
  flex: 1 1 420px;
}

.queue-filter {
  flex: 0 1 220px;
}

.toolbar-actions,
.row-actions {
  display: flex;
  align-items: center;
}

.orders-table-card {
  overflow: hidden;
  background: rgba(255, 255, 255, 0.025) !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
}

.orders-table {
  background: transparent !important;
}

.orders-table ::v-deep tbody tr {
  cursor: pointer;
  transition: background-color 140ms ease;
}

.orders-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.055) !important;
}

.reference-cell,
.date-cell,
.person-cell,
.summary-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.reference-cell strong,
.cell-strong {
  color: rgba(255, 255, 255, 0.92);
  font-weight: 600;
}

.reference-cell span,
.date-cell small,
.person-cell small,
.summary-cell small,
.muted-cell {
  overflow: hidden;
  color: rgba(255, 255, 255, 0.46);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-cell {
  display: flex;
  justify-content: center;
}

.queue-loading,
.queue-empty {
  border-radius: 12px;
}

.queue-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 290px;
  padding: 40px 20px;
  text-align: center;
  background: rgba(255, 255, 255, 0.025);
  border: 1px dashed rgba(255, 255, 255, 0.1);
}

.mobile-orders {
  display: grid;
  gap: 12px;
}

.mobile-order-card {
  padding: 15px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 13px;
  cursor: pointer;
}

.mobile-order-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.65);
  outline: none;
}

.mobile-order-top,
.mobile-order-summary,
.mobile-card-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.mobile-order-top {
  align-items: flex-start;
}

.eyebrow {
  color: rgba(255, 255, 255, 0.42) !important;
  font-size: 9px !important;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.mobile-order-meta {
  display: grid;
  gap: 7px;
  margin: 14px 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
}

.mobile-order-meta span {
  display: flex;
  align-items: center;
  gap: 7px;
}

.mobile-order-summary {
  padding: 11px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.055);
  border-bottom: 1px solid rgba(255, 255, 255, 0.055);
}

.mobile-order-summary > div {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.mobile-order-summary span {
  overflow: hidden;
  color: rgba(255, 255, 255, 0.48);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-card-actions {
  min-height: 48px;
  margin: 5px -8px -7px;
}

.touch-action {
  min-height: 44px;
}

@media (max-width: 1100px) {
  .status-kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .queue-toolbar {
    flex-wrap: wrap;
  }

  .queue-search {
    flex: 1 1 100%;
  }

  .queue-filter {
    flex: 1 1 260px;
  }
}

@media (max-width: 600px) {
  .queue-heading {
    align-items: center;
  }

  .queue-heading h2 {
    font-size: 1.25rem !important;
  }

  .queue-subtitle {
    display: none;
  }

  .status-kpis {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 1px 1px 7px;
    scroll-snap-type: x proximity;
  }

  .status-kpi {
    flex: 0 0 142px;
    min-height: 66px;
    scroll-snap-align: start;
  }

  .queue-toolbar {
    padding: 11px;
  }

  .queue-search,
  .queue-filter {
    flex: 1 1 100%;
  }

  .toolbar-actions {
    justify-content: flex-end;
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .status-kpi,
  .orders-table ::v-deep tbody tr {
    transition: none;
  }
}
</style>
